import { useGetAdminCoursesOverview } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import Select from '@components/V4/Select/Select';
import Tabs from '@components/V4/Tabs/Tabs';
import {
  BookOpenIcon,
  ExclamationIcon,
  EyeIcon,
  LinkIcon,
  PlusIcon,
  RefreshIcon,
} from '@heroicons/react/outline';
import {
  EmptyState,
  FilterField,
  MetricCard,
  SectionHeading,
  TableShell,
} from '@modules/Admin/Reporting/common';
import * as ROUTES from '@routes';
import {
  AdminCourseLmsStatus,
  AdminCourseRowDTO,
  LmsCatalogCourseDTO,
} from '@shared/apis/courses.apis';
import AgoraSearchBar from '@utils/AgoraSearchbar';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import CourseAssessment from './CourseAssessment';
import CourseCreateModal from './CourseCreateModal';
import { CourseFormValues } from './CourseForm';
import {
  CourseSwatch,
  FlagPill,
  LmsStatusBadge,
  formatUnitTypes,
  lmsAccessLabel,
} from './courseUi';

const headRow = 'border-b border-white/10 text-xs uppercase tracking-wide text-customGrey';
const bodyRow = 'border-b border-white/5 align-top transition-colors last:border-b-0';
const leftHead = 'px-4 py-3 text-left font-semibold';
const rightHead = 'px-4 py-3 text-right font-semibold';
const leftCell = 'px-4 py-3 text-left text-customLight';
const rightCell = 'px-4 py-3 text-right tabular-nums text-customLight';

type LmsFilter = 'all' | AdminCourseLmsStatus | 'problem';
type KindFilter = 'all' | 'mentored' | 'mentorless' | 'free' | 'paid';

const LMS_FILTER_OPTIONS: { label: string; value: LmsFilter }[] = [
  { label: 'Any LMS state', value: 'all' },
  { label: 'Needs attention (empty, missing, unlinked)', value: 'problem' },
  { label: 'Content ready', value: 'linked' },
  { label: 'LMS course empty', value: 'empty' },
  { label: 'LMS course missing', value: 'missing' },
  { label: 'Not linked', value: 'unlinked' },
];

const KIND_OPTIONS: { label: string; value: KindFilter }[] = [
  { label: 'Any kind', value: 'all' },
  { label: 'With mentor', value: 'mentored' },
  { label: 'Mentorless', value: 'mentorless' },
  { label: 'Free', value: 'free' },
  { label: 'Paid', value: 'paid' },
];

const PROBLEM_STATUSES: AdminCourseLmsStatus[] = ['empty', 'missing', 'unlinked'];

type PageTab = 'courses' | 'assessment' | 'lms';

const Courses = () => {
  const history = useHistory();
  const [refresh, setRefresh] = useState(false);
  const [search, setSearch] = useState('');
  const [lmsFilter, setLmsFilter] = useState<LmsFilter>('all');
  const [kind, setKind] = useState<KindFilter>('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [prefill, setPrefill] = useState<Partial<CourseFormValues>>();
  const [tab, setTab] = useState<PageTab>('courses');

  const [isScanning, setIsScanning] = useState(false);

  const { data, isLoading, isFetching } = useGetAdminCoursesOverview(
    { refresh },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchInterval: isScanning ? 4000 : false,
      onSuccess: (response) => {
        setRefresh(false);
        setIsScanning(!!response.lmsScan);
      },
    }
  );

  const courses = useMemo(() => data?.courses ?? [], [data]);
  const catalog = useMemo(() => data?.lmsCatalog ?? [], [data]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return courses.filter((course) => {
      if (lmsFilter === 'problem' && !PROBLEM_STATUSES.includes(course.lmsStatus)) return false;
      if (lmsFilter !== 'all' && lmsFilter !== 'problem' && course.lmsStatus !== lmsFilter)
        return false;
      if (kind === 'mentored' && course.isMentorless) return false;
      if (kind === 'mentorless' && !course.isMentorless) return false;
      if (kind === 'free' && !course.isFree) return false;
      if (kind === 'paid' && course.isFree) return false;
      if (!needle) return true;
      return [course.title, course.subtitle, course.abbreviation, course.lms?.title]
        .filter(Boolean)
        .some((text) => (text as string).toLowerCase().includes(needle));
    });
  }, [courses, search, lmsFilter, kind]);

  const unlinkedLms = useMemo(
    () =>
      catalog
        .filter((course) => !course.linkedCourseIds.length)
        .sort((a, b) => a.title.localeCompare(b.title)),
    [catalog]
  );

  const summary = useMemo(
    () => ({
      total: courses.length,
      mentorless: courses.filter((course) => course.isMentorless).length,
      ready: courses.filter((course) => course.lmsStatus === 'linked').length,
      problems: courses.filter((course) => PROBLEM_STATUSES.includes(course.lmsStatus)).length,
      emptyInLms: catalog.filter((course) => course.content?.isEmpty).length,
    }),
    [courses, catalog]
  );

  const openCourse = (course: AdminCourseRowDTO) =>
    history.push(`${ROUTES.MANAGE_COURSES}/${course._id}`);

  const createFromLms = (course: LmsCatalogCourseDTO) => {
    setPrefill({
      title: course.title,
      lmsCourseId: course.id,
      lmsCoursePrice: course.price,
      isFree: !course.price,
    });
    setIsCreateOpen(true);
  };

  if (isLoading || !data) {
    return (
      <div className="flex h-96 flex-col items-center justify-center gap-3 text-sm text-customGrey">
        <LoadingSpinner />
        <span>Loading courses and checking LMS content…</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 p-8 font-raleway text-white">
      <div className="flex flex-col gap-3 laptop:flex-row laptop:items-start laptop:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold">Courses</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="secondary"
            buttonText={isScanning ? 'Checking LMS…' : 'Re-check LMS'}
            icon={
              <RefreshIcon className={mergeClassNames('h-4 w-4', isScanning && 'animate-spin')} />
            }
            disabled={isScanning || (isFetching && refresh)}
            onClick={() => setRefresh(true)}
          />
          <Button
            buttonText="New course"
            icon={<PlusIcon className="h-4 w-4" />}
            onClick={() => {
              setPrefill(undefined);
              setIsCreateOpen(true);
            }}
          />
        </div>
      </div>

      {data.lmsScan ? (
        <div className="flex items-center gap-3 rounded-2xl bg-white/5 p-4 text-sm text-customGrey">
          <RefreshIcon className="h-5 w-5 shrink-0 animate-spin" />
          <span>
            Checking LMS content in the background: {data.lmsScan.done} of {data.lmsScan.total}{' '}
            courses. The LMS limits how fast we can ask, so this takes a minute or two. The page
            updates on its own.
          </span>
        </div>
      ) : null}

      {data.lmsError ? (
        <div className="flex items-start gap-3 rounded-2xl bg-customGold/10 p-4 text-sm text-customGold">
          <ExclamationIcon className="mt-0.5 h-5 w-5 shrink-0" />
          <span>
            The LMS could not be reached, so content checks are unavailable. {data.lmsError}
          </span>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 laptop:grid-cols-4">
        <MetricCard
          label="Courses in Agora"
          value={summary.total}
          icon={<BookOpenIcon className="h-5 w-5" />}
          accent="teal"
        />
        <MetricCard
          label="Mentorless courses"
          value={summary.mentorless}
          icon={<EyeIcon className="h-5 w-5" />}
          accent="lila"
          onClick={() => {
            setKind('mentorless');
            setTab('courses');
          }}
        />
        <MetricCard
          label="With LMS content ready"
          value={summary.ready}
          icon={<LinkIcon className="h-5 w-5" />}
          accent="green"
          onClick={() => {
            setLmsFilter('linked');
            setTab('courses');
          }}
        />
        <MetricCard
          label="LMS needs attention"
          value={summary.problems}
          icon={<ExclamationIcon className="h-5 w-5" />}
          accent="gold"
          onClick={() => {
            setLmsFilter('problem');
            setTab('courses');
          }}
        />
      </div>

      <Tabs<PageTab>
        tabs={[
          { label: `Courses (${courses.length})`, value: 'courses' },
          { label: 'Product assessment', value: 'assessment' },
          {
            label: `LMS courses not linked (${unlinkedLms.length})`,
            value: 'lms',
          },
        ]}
        activeTab={tab}
        onTabChange={setTab}
        renderContent={(current) => {
          if (current === 'assessment') {
            return <CourseAssessment />;
          }

          if (current === 'lms') {
            return data.lmsError ? (
              <EmptyState>The LMS could not be reached, so there is nothing to compare.</EmptyState>
            ) : (
              <section className="flex flex-col gap-4">
                <SectionHeading
                  title={`${unlinkedLms.length} LMS course${
                    unlinkedLms.length === 1 ? '' : 's'
                  } not used by any Agora course`}
                  description="Courses that exist in the LMS but no Agora course points to them. Create an Agora course from one, or ignore drafts and archived material."
                />
                {!unlinkedLms.length ? (
                  <EmptyState>Every LMS course is linked to an Agora course.</EmptyState>
                ) : (
                  <TableShell minWidth="min-w-[720px]">
                    <thead>
                      <tr className={headRow}>
                        <th className={leftHead}>LMS course</th>
                        <th className={leftHead}>Access</th>
                        <th className={leftHead}>Content</th>
                        <th className={rightHead}>Changed</th>
                        <th className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {unlinkedLms.map((course) => (
                        <tr key={course.id} className={bodyRow}>
                          <td className={leftCell}>
                            <div className="flex flex-col">
                              <span className="font-medium text-white">{course.title}</span>
                              <span className="text-xs text-customGrey">id {course.id}</span>
                            </div>
                          </td>
                          <td className={leftCell}>
                            <div className="flex gap-1.5">
                              <FlagPill tone={course.access === 'draft' ? 'warn' : 'neutral'}>
                                {lmsAccessLabel(course.access)}
                              </FlagPill>
                              {course.price ? <FlagPill>{course.price}$</FlagPill> : null}
                            </div>
                          </td>
                          <td className={mergeClassNames(leftCell, 'text-xs')}>
                            {course.contentError ? (
                              <span className="text-customGold">{course.contentError}</span>
                            ) : course.content?.isEmpty ? (
                              <span className="text-customRed">No learning units</span>
                            ) : course.content ? (
                              <span className="text-customGrey">
                                {course.content.unitCount} units ·{' '}
                                {formatUnitTypes(course.content.unitsByType)}
                              </span>
                            ) : (
                              <span className="text-customGrey">
                                {data.lmsScan ? 'Checking…' : 'Not checked'}
                              </span>
                            )}
                          </td>
                          <td className={mergeClassNames(rightCell, 'text-xs text-customGrey')}>
                            {course.modifiedAt
                              ? moment(course.modifiedAt).format('D MMM YYYY')
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              className="text-xs text-customPrimary hover:underline"
                              onClick={() => createFromLms(course)}
                            >
                              Create Agora course
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </TableShell>
                )}
              </section>
            );
          }

          return (
            <section className="flex flex-col gap-4">
              <SectionHeading
                title={`${filtered.length} of ${courses.length} courses`}
                description={
                  data.lmsFetchedAt
                    ? `LMS content last checked ${moment(
                        data.lmsFetchedAt
                      ).fromNow()}. Click a course to edit it.`
                    : 'Click a course to edit it.'
                }
              />

              <div className="flex flex-wrap items-end gap-4">
                <FilterField width="w-full tablet:w-72">
                  <AgoraSearchBar
                    search={search}
                    handleSearch={setSearch}
                    placeholder="Search by title, abbreviation or LMS course..."
                  />
                </FilterField>
                <FilterField width="w-full tablet:w-64">
                  <Select
                    label="LMS content"
                    variant="secondary"
                    value={lmsFilter}
                    options={LMS_FILTER_OPTIONS}
                    onSelect={(value) => setLmsFilter(value as LmsFilter)}
                  />
                </FilterField>
                <FilterField width="w-full tablet:w-44">
                  <Select
                    label="Kind"
                    variant="secondary"
                    value={kind}
                    options={KIND_OPTIONS}
                    onSelect={(value) => setKind(value as KindFilter)}
                  />
                </FilterField>
              </div>

              {!filtered.length ? (
                <EmptyState>No courses match the current filters.</EmptyState>
              ) : (
                <TableShell minWidth="min-w-[1080px]">
                  <thead>
                    <tr className={headRow}>
                      <th className={leftHead}>Course</th>
                      <th className={leftHead}>Access</th>
                      <th className={rightHead}>Students</th>
                      <th className={leftHead}>LMS content</th>
                      <th className={leftHead}>Legacy content</th>
                      <th className={rightHead}>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((course) => {
                      const legacyItems = course.legacy.lessons + course.legacy.quizzes;
                      return (
                        <tr
                          key={course._id}
                          className={mergeClassNames(bodyRow, 'cursor-pointer hover:bg-white/5')}
                          onClick={() => openCourse(course)}
                        >
                          <td className={leftCell}>
                            <div className="flex items-center gap-3">
                              <CourseSwatch
                                color={course.color}
                                abbreviation={course.abbreviation}
                              />
                              <div className="flex min-w-0 flex-col">
                                <span className="font-semibold text-white">{course.title}</span>
                                {course.subtitle ? (
                                  <span className="truncate text-xs text-customGrey">
                                    {course.subtitle}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td className={leftCell}>
                            <div className="flex flex-wrap gap-1.5">
                              <FlagPill>
                                {course.isFree
                                  ? 'Free'
                                  : course.price
                                  ? `${course.price} €`
                                  : 'Paid'}
                              </FlagPill>
                              {course.isMentorless ? (
                                <FlagPill tone="warn">Mentorless</FlagPill>
                              ) : null}
                              {course.country ? <FlagPill>{course.country}</FlagPill> : null}
                            </div>
                          </td>
                          <td className={rightCell}>
                            <div className="flex flex-col items-end">
                              <span className="text-white">{course.enrollments.active}</span>
                              <span className="text-xs text-customGrey">
                                {course.enrollments.total} total · {course.enrollments.mentors}{' '}
                                mentor
                                {course.enrollments.mentors === 1 ? '' : 's'}
                              </span>
                            </div>
                          </td>
                          <td className={leftCell}>
                            <div className="flex flex-col gap-1">
                              <LmsStatusBadge status={course.lmsStatus} className="self-start" />
                              {course.lms ? (
                                <span className="max-w-xs truncate text-xs text-customGrey">
                                  {course.lms.title} · {lmsAccessLabel(course.lms.access)}
                                  {course.lms.content
                                    ? ` · ${course.lms.content.unitCount} units${
                                        course.lms.content.unitCount
                                          ? ` (${formatUnitTypes(course.lms.content.unitsByType)})`
                                          : ''
                                      }`
                                    : ''}
                                </span>
                              ) : course.lmsCourseId ? (
                                <span className="text-xs text-customGrey">
                                  id {course.lmsCourseId}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td className={mergeClassNames(leftCell, 'text-xs text-customGrey')}>
                            {course.legacy.modules
                              ? `${course.legacy.modules} module${
                                  course.legacy.modules === 1 ? '' : 's'
                                }, ${legacyItems} item${legacyItems === 1 ? '' : 's'}`
                              : '—'}
                          </td>
                          <td className={mergeClassNames(rightCell, 'text-xs text-customGrey')}>
                            {course.updatedAt ? moment(course.updatedAt).format('D MMM YYYY') : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </TableShell>
              )}
            </section>
          );
        }}
      />

      <CourseCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={(courseId) => {
          setIsCreateOpen(false);
          history.push(`${ROUTES.MANAGE_COURSES}/${courseId}`);
        }}
        lmsCatalog={catalog}
        isLmsCatalogLoading={isFetching}
        prefill={prefill}
      />
    </div>
  );
};

export default Courses;
